import { useCallback, useEffect, useRef, useState } from 'react';
import { mergeRows } from './conversationClient';

export default function useConversationPage(api, path, field = 'id', messagePage = false) {
  const [page, setPage] = useState({ rows: [], loading: true, error: null, data: null });
  const state = useRef(page), generation = useRef(0), controllers = useRef(new Set()), busy = useRef(false);
  const update = useCallback(fn => setPage(previous => { const next = fn(previous); state.current = next; return next; }), []);
  const load = useCallback(async (older = false) => {
    if (!path || busy.current || (older && !state.current.nextCursor)) return;
    const stamp = generation.current, controller = new AbortController(); controllers.current.add(controller); busy.current = true;
    update(previous => ({ ...previous, loading: true, error: null }));
    try {
      const cursor = older ? state.current.nextCursor : null;
      const data = await api(`${path}${path.includes('?') ? '&' : '?'}limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, { signal: controller.signal });
      if (stamp !== generation.current) return;
      update(previous => ({ rows: mergeRows(previous.rows, messagePage ? data.messages : data.items, field), data, loading: false, error: null,
        nextCursor: !older && previous.olderLoaded ? previous.nextCursor : data.nextCursor,
        hasMore: !older && previous.olderLoaded ? previous.hasMore : data.hasMore, olderLoaded: older || previous.olderLoaded }));
    } catch (error) { if (!controller.signal.aborted && stamp === generation.current) update(previous => ({ ...previous, loading: false, error: error.message })); }
    finally { controllers.current.delete(controller); if (stamp === generation.current) busy.current = false; }
  }, [api, path, field, messagePage, update]);
  useEffect(() => {
    generation.current++; busy.current = false;
    update(() => ({ rows: [], loading: Boolean(path), error: null, data: null })); load();
    const timer = setInterval(() => { if (document.visibilityState !== 'hidden') load(); }, 45000);
    const visible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', visible);
    return () => { generation.current++; controllers.current.forEach(c => c.abort()); controllers.current.clear(); clearInterval(timer); document.removeEventListener('visibilitychange', visible); };
  }, [path, load, update]);
  return { ...page, refresh: () => load(), older: () => load(true), update };
}
